/**
 * A vending machine's own lifecycle — separate from whether it's currently
 * online. `retired` means "gone from the fleet", used instead of deleting the
 * row so past orders and vend attempts keep a machine to point at.
 */
export const VENDING_MACHINE_STATUSES = ['active', 'disabled', 'retired'];
/**
 * The single-use authorization to dispense one item, one order. Almost every
 * order-fulfilment state machine in this codebase is linear; this one has a
 * single legal edge out of a terminal state — `timed_out -> dispensed` — for
 * the case where the Pi's result arrives after the timeout job already gave
 * up. A customer who paid must get their product even if the "we gave up
 * waiting" and "it actually worked" messages cross in flight.
 */
export const VEND_ATTEMPT_STATUSES = [
    'pending',
    'dispatched',
    'dispensed',
    'failed',
    'timed_out',
];
export const VEND_ATTEMPT_TRANSITIONS = {
    pending: ['dispatched', 'failed', 'timed_out'],
    dispatched: ['dispensed', 'failed', 'timed_out'],
    dispensed: [],
    failed: [],
    timed_out: ['dispensed'],
};
export function canTransitionVendAttempt(from, to) {
    return VEND_ATTEMPT_TRANSITIONS[from].includes(to);
}
/** Truly final for every purpose except the late-success edge above. */
export const TERMINAL_VEND_ATTEMPT_STATUSES = ['dispensed', 'failed'];
export const MACHINE_ISSUE_KINDS = [
    'vend_failed',
    'vend_timed_out',
    'pi_offline',
    'reported',
    'customer_reported',
    // Glass-cabinet machines (t-cabinet). Appended, never inserted: a Postgres
    // enum's `ADD VALUE` always appends.
    'cabinet_timed_out',
    'cabinet_not_locked',
    'cabinet_mismatch',
    // The cabinet pickup finished but the held card could not be charged.
    'cabinet_capture_failed',
    // Every paying customer has seen the cabinet code; this one says it is
    // overdue for a change. Resolved by changing the code.
    'cabinet_code_due',
];
/** How long a cabinet code may stay unchanged before staff are reminded. */
export const CABINET_CODE_ROTATION_DAYS = 7;
export const MACHINE_ISSUE_STATUSES = ['open', 'resolved'];
/**
 * What kind of machine a `vending_machines` row is. `vending` dispenses from
 * motorised slots driven by a Pi. `cabinet` is a combination-locked glass
 * cabinet next to a wall tablet: the customer pays, is shown the code, takes
 * the items themselves and scans each one so the sale can be checked against
 * what was paid for. A cabinet has no Pi and no slots — it sells whatever its
 * location has in stock.
 */
export const MACHINE_KINDS = ['vending', 'cabinet'];
/**
 * One customer collecting one paid order from a glass cabinet.
 *
 * `open` starts the moment payment settles and the code is shown. Scanning
 * every item on the order confirms it. `timed_out` is raised by a job when a
 * customer opened the cabinet and never finished scanning — the same "late
 * success" edge as `VEND_ATTEMPT_TRANSITIONS`: a customer who scans after
 * the timeout fired still completes, since they paid and the goods match.
 * `cancelled` is staff-only (a refund).
 */
export const CABINET_PICKUP_STATUSES = ['open', 'confirmed', 'timed_out', 'cancelled'];
export const CABINET_PICKUP_TRANSITIONS = {
    open: ['confirmed', 'timed_out', 'cancelled'],
    confirmed: [],
    timed_out: ['confirmed', 'cancelled'],
    cancelled: [],
};
export function canTransitionCabinetPickup(from, to) {
    return CABINET_PICKUP_TRANSITIONS[from].includes(to);
}
/** Statuses in which the customer may still scan, and still sees the code. */
export const SCANNABLE_CABINET_PICKUP_STATUSES = [
    'open',
    'timed_out',
];
/**
 * The outcome of one barcode scan, recorded whether it matched or not —
 * the scan log is the evidence trail for a disputed pickup.
 */
export const CABINET_SCAN_RESULTS = [
    'matched',
    /** A product or device that is real but not on this order (or is a
     *  different box of the right model than the one the order holds). */
    'not_in_cart',
    /** Already scanned as many of this item as the order is for. */
    'already_scanned',
    /** Matches nothing the system knows. */
    'unknown_code',
];
/** The moments a cabinet tablet grabs a still from the store's IP cameras. */
export const CABINET_SNAPSHOT_EVENTS = [
    'code_shown',
    'scan',
    'mismatch',
    'lock_confirmed',
    'lock_refused',
    'timed_out',
];
/** Whether the customer said they locked the cabinet again afterwards. */
export const CABINET_LOCK_STATES = ['locked', 'not_locked'];
//# sourceMappingURL=vend-states.js.map